import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { StyleSheet } from 'react-native';
import Animated, { FadeIn, FadeOut, useAnimatedStyle, withTiming } from 'react-native-reanimated';

import { theme } from '@garnish/constants';

import { BodyText, Icon, LoadingDots } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const ReorderRailCardStatusOverlay = (props: Props) => {
  const { status, visible } = props;

  const isSuccess = status === 'success';

  // ─── Styles ──────────────────────────────────────────────────────────

  const backdropAnimatedStyles = useAnimatedStyle(
    () => ({ opacity: withTiming(visible ? 1 : 0, { duration: theme.transitions.base }) }),
    [visible],
  );

  const backdropStyles = [
    styles.backdrop,
    isSuccess ? styles.backdropSuccess : styles.backdropDefault,
    backdropAnimatedStyles,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Animated.View pointerEvents="none" style={backdropStyles}>
      {status === 'idle' ? (
        <Animated.View entering={ENTERING} exiting={EXITING} style={styles.control}>
          <Icon name="IconPlus2" color={theme.colors.WHITE} width={ICON_SIZE} height={ICON_SIZE} />
        </Animated.View>
      ) : null}

      {status === 'loading' ? (
        <Animated.View entering={ENTERING} exiting={EXITING} style={styles.control}>
          <LoadingDots color={theme.colors.WHITE} />
        </Animated.View>
      ) : null}

      {status === 'success' ? (
        <Animated.View entering={ENTERING} exiting={EXITING} style={styles.successPill}>
          <Icon name="IconCheck" color={theme.colors.KALE} width={16} height={16} />

          <BodyText sizeMatch={['12', '16']} style={styles.successLabel}>
            <FormattedMessage {...messages.addedToBag} />
          </BodyText>
        </Animated.View>
      ) : null}
    </Animated.View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  addedToBag: {
    defaultMessage: 'Added to bag',
    description: 'Menu | Reorder rail | Card | Added to bag label',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const CONTROL_SIZE = 54;
const ICON_SIZE = 37;

const ENTERING = FadeIn.duration(theme.transitions.base);
const EXITING = FadeOut.duration(theme.transitions.base);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  backdropDefault: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  backdropSuccess: {
    backgroundColor: theme.colors.OPACITY.KALE.LIGHTEST,
  },
  control: {
    alignItems: 'center',
    justifyContent: 'center',
    width: CONTROL_SIZE,
    height: CONTROL_SIZE,
    borderRadius: CONTROL_SIZE,
    backgroundColor: theme.colors.SPINACH,
  },
  successPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['1'],
    paddingVertical: theme.spacing['3'],
    paddingHorizontal: theme.spacing['6'],
    borderRadius: theme.radius.xlarge,
    backgroundColor: theme.colors.WHITE,
  },
  successLabel: {
    color: theme.colors.KALE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ReorderRailCardStatus = 'idle' | 'loading' | 'success';

type Props = {
  status: ReorderRailCardStatus;
  visible: boolean;
};
